import React, { createContext, useContext, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';

const WLCContext = createContext(undefined);

/**
 * WLCProvider – Top-level context for WLC product state.
 *
 * Distributes tour / onboarding state plus navigation helpers so that
 * deeply nested pages (Dashboard, CurrentWeek, wildcard routes) can
 * consume them without prop drilling.
 *
 * Expects the controlling hook (useWLCData) to supply values via props.
 */
export const WLCProvider = ({
  children,
  isOnboard,
  toursCompleted,
  loadingState,
  gettingStartedCompleted,
  wlcProgress,
  completeOnboarding,
  updateTourStatus,
  updateWlcProgress,
  handleNav,
  productSlug,
  getAuthToken,
  authToken
}) => {
  /** Whether tour data has finished loading (inverse of loadingState). */
  const toursLoaded = useMemo(() => !loadingState, [loadingState]);

  /** Convenience: mark a specific tour complete. */
  const completeTour = useCallback(tour => updateTourStatus(tour, true), [updateTourStatus]);
  const _completeOnboarding = useCallback(() => completeOnboarding(true), [completeOnboarding]);

  const value = useMemo(
    () => ({
      // Tour / onboarding
      toursCompleted,
      toursLoaded,
      loadingState,
      isOnboard,
      gettingStartedCompleted,
      updateTourStatus,
      completeTour,
      completeOnboarding: _completeOnboarding,

      // Track progress
      wlcProgress,
      updateWlcProgress,

      // Navigation
      handleNav,
      productSlug,

      // Auth
      getAuthToken,
      authToken
    }),
    [
      toursCompleted,
      toursLoaded,
      loadingState,
      isOnboard,
      gettingStartedCompleted,
      updateTourStatus,
      _completeOnboarding,
      completeTour,
      wlcProgress,
      updateWlcProgress,
      handleNav,
      productSlug,
      getAuthToken,
      authToken
    ]
  );

  return <WLCContext.Provider value={value}>{children}</WLCContext.Provider>;
};

WLCProvider.propTypes = {
  children: PropTypes.node.isRequired,
  toursCompleted: PropTypes.shape({}).isRequired,
  loadingState: PropTypes.bool.isRequired,
  gettingStartedCompleted: PropTypes.bool.isRequired,
  wlcProgress: PropTypes.shape({
    currentTrack: PropTypes.string,
    currentWeekSlug: PropTypes.string,
    trackPriorityOrder: PropTypes.arrayOf(PropTypes.string),
    trackProgress: PropTypes.shape({})
  }),
  updateTourStatus: PropTypes.func.isRequired,
  updateWlcProgress: PropTypes.func.isRequired,
  handleNav: PropTypes.func.isRequired,
  productSlug: PropTypes.string.isRequired,
  getAuthToken: PropTypes.func.isRequired,
  authToken: PropTypes.string
};

/**
 * useWLC – Consumer hook for WLC context.
 *
 * @returns {Object} WLC context value
 * @throws {Error} If used outside a WLCProvider
 */
export const useWLC = () => {
  const context = useContext(WLCContext);

  if (context === undefined) {
    throw new Error('useWLC must be used within a WLCProvider');
  }

  return context;
};

/**
 * withWLC – HOC to inject WLC context as a `wlc` prop.
 * Useful for class components that cannot use hooks directly.
 */
export const withWLC = Component => {
  const WrappedComponent = props => {
    const wlcContext = useWLC();
    return <Component {...props} wlc={wlcContext} />;
  };

  WrappedComponent.displayName = `withWLC(${Component.displayName || Component.name || 'Component'})`;

  return WrappedComponent;
};

export default WLCContext;
